import { useQueryClient } from "@tanstack/react-query";
import { type RefObject, useRef } from "react";
import { toast } from "sonner";
import { removeOptimisticChatMessage } from "web_myplann/Chat/Helpers/chatMessagesCache";
import { restoreChatAttachmentFile } from "web_myplann/Chat/Helpers/restoreChatAttachmentFile";
import type { ChatMessageView } from "web_myplann/Chat/Types/chatMessage";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { AttachmentPicker } from "web_myplann/Shared/Types/AttachmentPicker";
import { createId } from "web_myplann/Shared/Utils/createId";
import { runAttachmentPicker } from "web_myplann/Shared/Utils/runAttachmentPicker";
import { device } from "web_myplann/Utils/device";
import { AttachmentPickStatuses } from "web_myplann/Utils/WebViewModel";

type Args = {
	conversationIdRef: RefObject<number | null>;
	takeCaption: () => string;
	sendText: (content: string, clientId: string) => void;
	sendAttachment: (
		file: File,
		caption: string,
		clientId: string,
		callbacks: { onSettled: () => void },
	) => void;
	onMessageSent: () => void;
};

export const useChatAttachments = ({
	conversationIdRef,
	takeCaption,
	sendText,
	sendAttachment,
	onMessageSent,
}: Args) => {
	const queryClient = useQueryClient();
	const { t } = useResource();
	const pendingFilesRef = useRef(new Map<string, File>());

	const sendTrackedAttachment = (
		file: File,
		caption: string,
		clientId: string,
	) => {
		pendingFilesRef.current.set(clientId, file);

		sendAttachment(file, caption, clientId, {
			onSettled: () => pendingFilesRef.current.delete(clientId),
		});
		onMessageSent();
	};

	const sendPickedAttachment = async (picker: AttachmentPicker) => {
		if (conversationIdRef.current === null) return;

		const picked = await runAttachmentPicker(picker);

		if (picked.status === AttachmentPickStatuses.cancelled) return;

		if (picked.status === AttachmentPickStatuses.error) {
			toast.error(t(Resources.Chat.sendError));
			return;
		}

		sendTrackedAttachment(picked.file, takeCaption(), createId());
	};

	const handleRetry = async (message: ChatMessageView) => {
		const clientId = message.clientId;
		if (clientId === undefined) return;

		const attachment = message.attachments.at(0);
		const cachedFile = pendingFilesRef.current.get(clientId);
		const file = attachment
			? await restoreChatAttachmentFile(attachment, cachedFile)
			: undefined;

		if (attachment && !file) {
			toast.error(t(Resources.Chat.sendError));
			return;
		}

		removeOptimisticChatMessage(
			queryClient,
			conversationIdRef.current,
			clientId,
		);
		if (attachment?.localUrl) URL.revokeObjectURL(attachment.localUrl);

		if (file) {
			sendTrackedAttachment(file, message.content, clientId);
			return;
		}

		sendText(message.content, clientId);
		onMessageSent();
	};

	return {
		onRetry: handleRetry,
		onPickPhoto: () => sendPickedAttachment(device.pickAttachmentPhoto),
		onPickGallery: () => sendPickedAttachment(device.pickAttachmentGallery),
		onPickFile: () => sendPickedAttachment(device.pickAttachmentFile),
	};
};
